import { KeyboardEvent, MouseEvent } from 'react';
import { DynamicDataAttributes, GaEventsProps } from '@/utils/gaTypes';
import GA_EVENTS from '@/utils/gaEvents';
import { ProductEcommerceData } from '../store/types/ICart';

export const trackEvent = (
  event:
    | MouseEvent<HTMLAnchorElement>
    | MouseEvent<HTMLButtonElement>
    | MouseEvent<HTMLDivElement>
    | MouseEvent<HTMLSpanElement>
    | KeyboardEvent<HTMLAnchorElement>,
  isMedia: boolean = false
) => {
  if (
    isMedia === false &&
    event.currentTarget['host'] &&
    event?.currentTarget['host'] !== (window as any).location.host
  ) {
    trackExternalEvents(event.currentTarget['href']);
    return;
  }

  const attributes = event.currentTarget.dataset as DynamicDataAttributes;

  const eventInteraction = attributes['noninteract'] ? attributes['noninteract'] === 'true' : true;

  const gaEvents = getGaEventsFromAttributes(attributes);

  gaEvents.forEach(item => {
    sendGaEvent(item, eventInteraction);
  });
};

export const getGaEventsFromAttributes = (attributes: DynamicDataAttributes): DynamicDataAttributes[] => {
  const attributesKeys = Object.keys(attributes);

  // filter the data attributes to get all GA events
  const eventsKey = attributesKeys.filter(item => item.startsWith('event'));

  const eventsProperties = eventsKey.reduce((prev, curr) => {
    const subs = curr.split(/([0-9]+)/g);

    const eventName = subs[0] + subs[1];

    const property = subs[2];

    if (!prev[eventName]) {
      prev[eventName] = {};
    }

    prev[eventName][property.toLowerCase()] = attributes[curr];

    return prev;
  }, {});

  // Event list with a proper structure
  const eventList = Object.keys(eventsProperties).map(item => eventsProperties[item]);

  return eventList || [];
};

export const sendGaEvent = (attributes: DynamicDataAttributes, eventInteraction = true) => {
  const attributesKeys = Object.keys(attributes);

  const eventData = GA_EVENTS.find(item => item.attributes.every(element => attributesKeys.includes(element)));
  if (eventData) {
    eventData?.event(attributes, eventInteraction);
  }
};

const trackExternalEvents = (url: string) => {
  (window as any).dataLayer.push({
    event: 'trackEvent',
    eventCategory: 'external',
    eventAction: 'click',
    eventLabel: url,
    eventInteraction: true,
  });
};

export const handleGaClick = (
  element: MouseEvent<HTMLAnchorElement>,
  eventName: string,
  url: string,
  linkText: string,
  location: string
) => {
  (window as any).homePageDataPush(eventName, url, linkText, location);
  trackEvent(element);
};

// This two functions come from the Aragon tracking of the SFDE chat
export function trackEventForGA4(eventName: string, ga4Data?: Record<string, any>) {
  const data: Record<string, any> = { ...ga4Data };

  if (data && data.chat_origin !== undefined) {
    if (data.chat_origin === '' || data.chat_origin === 'other') {
      delete data.chat_origin;
    }
  }

  data.event = eventName;
  (window as any).dataLayer.push(data);
}

export function trackEventDirect(category: string, action: string, label: string, interaction?: boolean | undefined) {
  (window as any).dataLayer.push({
    event: 'trackEvent',
    eventCategory: category,
    eventAction: action,
    eventLabel: label,
    eventInteraction: interaction,
  });
}

export function trackEventWithObject(
  eventParams: { event: string; [key: string]: string | Object | undefined },
  interaction?: boolean
) {
  const data: { [key: string]: string | boolean } = {
    ...eventParams,
    event: eventParams.event || 'trackEvent',
  };
  if (interaction !== undefined) data.eventInteraction = interaction;

  (window as any).dataLayer.push(data);
}

export function trackEventWithGaEvents(eventParams: GaEventsProps, interaction: boolean = false) {
  // Ensure dataAttributes is iterable and not empty
  if (!Array.isArray(eventParams.dataAttributes) || eventParams.dataAttributes.length === 0) {
    return; // Exit early if no data to push
  }

  const data = eventParams.dataAttributes.reduce(
    (acc, { dataAttributeName, dataAttributeValue }) => {
      acc[dataAttributeName] = dataAttributeValue;
      return acc;
    },
    {} as { [key: string]: string | boolean }
  );

  if (interaction) {
    data.eventInteraction = interaction;
  }

  (window as any).dataLayer.push(data);
}

interface GA4ItemData {
  item_id: string;
  item_name: string;
  price: string;
  item_variant?: string;
  item_brand: string;
  item_category?: string;
  item_category1?: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  [key: string]: any;
}

const getGA4GenderForRing = (category: string, genderForRing?: string): string => {
  if (category === 'Wedding Bands') {
    return genderForRing === 'M' ? 'Mens Wedding Band' : 'Womens Wedding Bands';
  }
  return genderForRing || '';
};

export const buildGA4EcommerceData = (
  ecommerceData: ProductEcommerceData | ProductEcommerceData[],
  currCountry: string,
  extraData: Record<string, string> = {}
): { ecommerce: any } => {
  const originalEeData = Array.isArray(ecommerceData) ? ecommerceData : [ecommerceData];
  const itemsData: GA4ItemData[] = [];
  let subTotal = 0;

  originalEeData.forEach((item, index) => {
    const categoryLevels = item.category?.split('/') || [];
    const price = typeof item.price === 'string' ? parseFloat(item.price) : item.price;
    const itemData: GA4ItemData = {
      style: item.dimension17 || undefined,
      item_id: item.product_id || item.id,
      item_name: item.name,
      price: price.toFixed(2),
      item_variant: item.variant || undefined,
      item_brand: item.brand || 'Brilliant Earth',
      diamond_carat: item.dimension12,
      diamond_cut: item.dimension13,
      diamond_color: item.dimension14,
      diamond_clarity: item.dimension15,
      gemstone_shape: item.dimension16,
      item_sku: item.id,
      index: item.index ?? index + 1,
      ...Object.fromEntries(
        Array(5)
          .fill(0)
          .map((_, i) => [`item_category${(i > 0 ? i + 1 : i) || ''}`, categoryLevels[i] || ''])
      ),
    };
    itemData.scs_certified = item.dimension23 ? item.dimension23.toLowerCase() : '';
    itemData.certificate_of_origin = item.dimension24 ? item.dimension24.toLowerCase() : '';
    if (item.quantity) {
      itemData.quantity = item.quantity;
    }

    itemData.ring_gender = getGA4GenderForRing(categoryLevels[0], item.dimension18);
    if (['Diamonds', 'Colored Gemstones'].includes(categoryLevels[0])) {
      itemData.blockchain = item.dimension20 ? item.dimension20.toLowerCase() : '';
      itemData.diamond_dor = item.dimension21 ? item.dimension21.toLowerCase() : '';
      itemData.diamond_ha = item.dimension22 ? item.dimension22.toLowerCase() : '';
      itemData.fluorescence = item.dimension25 ? item.dimension25.toLowerCase() : '';
    }

    subTotal += price;
    itemsData.push(itemData);
  });

  return {
    ecommerce: {
      affiliation: currCountry === 'united states' ? 'domestic' : 'international',
      currency: document.cookie.match(/be_symbol=([^;]+)/)?.[1] || 'USD',
      value: subTotal.toFixed(2),
      items: itemsData,
      ...extraData,
    },
  };
};

let dataLayerListeners: Function[];
export function addDataLayerListener(newListener: Function) {
  // One time initializer
  if (!dataLayerListeners) {
    dataLayerListeners = [];
    const Window = window as any;
    Window.dataLayer = Window.dataLayer || [];

    // Saves and overrides dataLayer.push function
    const originalPush = Window.dataLayer.push;
    Window.dataLayer.push = (...args) => {
      // Push obj into dataLayer
      originalPush.apply(Window.dataLayer, args);

      dataLayerListeners?.forEach(listener => {
        try {
          listener(...args);
        } catch (e: any) {
          console.log('DataLayer listener error', e.toString());
        }
      });
    };
  }

  // Register new listener
  dataLayerListeners.push(newListener);
}

export function clearDataLayerListener(listener: Function) {
  dataLayerListeners = dataLayerListeners?.filter(l => l !== listener);
}
